import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { StaffSupportDock } from '../apps/creator/ux/components/StaffSupportDock';
import { CreatorSupportWidget } from '../apps/creator/ux/components/CreatorSupportWidget';
import { useCreatorAuth } from '../apps/creator/ux/hooks/useCreatorAuth';
import { AuthProvider } from '../features/login/ux/hooks/useAuth';
import { ProtectedRoute } from './ProtectedRoute';
import { usePermissions } from './hooks/usePermissions';
import { Toaster } from '@/web/components/ui/sonner';
import { CreatorAuthProvider } from '../apps/creator/ux/hooks/useCreatorAuth';
import {
  RequireOnboarded,
  RequireOnboarding,
} from '../apps/creator/ux/components/RequireOnboarded';
import MarketingScreen from '../features/marketing/marketing.screen';
import LoginScreen from '../features/login/login.screen';

/**
 * The pipeline is the app — it is what the team opens every morning, so it is
 * the landing screen after sign-in. Both authenticated screens are lazy so a
 * logged-out visitor only downloads the marketing page and the login form.
 */
const PipelineScreen = lazy(() => import('../features/pipeline/pipeline.screen'));
const SettingsScreen = lazy(() => import('../features/settings/settings.screen'));
const ChromeExtensionScreen = lazy(() => import('../features/gmail-extension/chrome-extension.screen'));
const RevenueScreen = lazy(() => import('../features/revenue/revenue.screen'));
const VideosScreen = lazy(() => import('../features/tracking/videos.screen'));
const InboundScreen = lazy(() => import('../features/inbound/inbound.screen'));
const JobScreen = lazy(() => import('../features/jobs/job.screen'));
const ApplicationsScreen = lazy(() => import('../features/jobs/applications.screen'));
const SupportScreen = lazy(() => import('../apps/creator/ux/screens/staff-support.screen'));

/**
 * The creator app (src/apps/creator). Lazy for the same reason as the CRM
 * screens: a visitor to the marketing page should download neither app.
 */
const CreatorLoginScreen = lazy(() => import('../apps/creator/ux/screens/login.screen'));
const CreatorResetPasswordScreen = lazy(() => import('../apps/creator/ux/screens/reset-password.screen'));
const CreatorJoinScreen = lazy(() => import('../apps/creator/ux/screens/join.screen'));
const CreatorBookCallScreen = lazy(() => import('../apps/creator/ux/screens/book-call.screen'));
const CreatorWatchVideoScreen = lazy(
  () => import('../apps/creator/ux/screens/watch-video.screen'),
);
const CreatorVideosScreen = lazy(() => import('../apps/creator/ux/screens/videos.screen'));
const CreatorPaymentsScreen = lazy(() => import('../apps/creator/ux/screens/payments.screen'));
const CreatorContractsScreen = lazy(() => import('../apps/creator/ux/screens/contracts.screen'));
const CreatorProductsScreen = lazy(() => import('../apps/creator/ux/screens/products.screen'));
const CreatorSupportScreen = lazy(() => import('../apps/creator/ux/screens/support.screen'));
const CreatorSignScreen = lazy(() => import('../apps/creator/ux/screens/sign-contract.screen'));
// Public: a creator reading a brand's brief, linked from a DM or an email.
// Lazy because most visitors to / never open one.
const BrandBriefScreen = lazy(() => import('../features/marketing/brand-brief.screen'));
const EmployeeOnboardingScreen = lazy(
  () => import('../features/employee-onboarding/onboarding.screen'),
);
const EmployeeOnboardingAdminScreen = lazy(
  () => import('../features/employee-onboarding/admin.screen'),
);

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<MarketingScreen />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route
            path="/employee/onboarding"
            element={
              <Suspense fallback={<ScreenFallback />}>
                <EmployeeOnboardingScreen />
              </Suspense>
            }
          />
          <Route path="/marketing/onboarding" element={<Navigate to="/employee/onboarding" replace />} />
          <Route path="/job" element={<Suspense fallback={<ScreenFallback />}><JobScreen /></Suspense>} />
          <Route path="/applications" element={
            <ProtectedRoute requires="applications.view">
              <Suspense fallback={<ScreenFallback />}><ApplicationsScreen /></Suspense>
            </ProtectedRoute>
          } />

          {/* Public creator brief. Outside ProtectedRoute for the same
              reason as /sign: the person opening it has no account, is
              deciding whether to take the deal, and a login wall is how
              that decision becomes a no. */}
          <Route
            path="/brand/:brandName"
            element={
              <Suspense fallback={<ScreenFallback />}>
                <BrandBriefScreen />
              </Suspense>
            }
          />

          {/*
            The old public signing URL, kept as a redirect.

            Every contract emailed before this change carries a /sign/:token
            link, and those emails sit in creators' inboxes forever. Deleting
            the route would turn each of them into a 404 for somebody who was
            asked to sign something — so it forwards to the account step, which
            reads the same token and routes onward by itself.

            Nothing is served here: the page is gone and the API behind it is
            gone. This is a signpost, not a bypass.
          */}
          <Route path="/sign/:token" element={<LegacySignRedirect />} />

{/*
            ── The creator app ──────────────────────────────────────────────

            Wrapped in its OWN auth provider. Creator identity is a different
            cookie backed by a different table, and keeping the two providers
            disjoint means no component can read `isAuthenticated` and get the
            wrong kind of user.

            Three tiers, mirroring the router on the server:

              · /creator/login, /creator/join/:token  — no session needed
              · /creator/book-call                    — signed in, onboarding
              · everything else                       — signed in and onboarded

            `RequireOnboarded` is a router convenience, NOT the security
            boundary — `requireOnboarded` in Express is. See that component.
          */}
          <Route
            path="/creator/login"
            element={
              <CreatorAuthProvider>
                <Suspense fallback={<ScreenFallback />}>
                  <CreatorLoginScreen />
                </Suspense>
              </CreatorAuthProvider>
            }
          />
          <Route
            path="/creator/join/:token"
            element={
              <CreatorAuthProvider>
                <Suspense fallback={<ScreenFallback />}>
                  <CreatorJoinScreen />
                </Suspense>
              </CreatorAuthProvider>
            }
          />
          <Route
            path="/creator/reset-password"
            element={
              <Suspense fallback={<ScreenFallback />}>
                <CreatorResetPasswordScreen />
              </Suspense>
            }
          />
          <Route
            path="/creator/activate"
            element={
              <Suspense fallback={<ScreenFallback />}>
                <CreatorResetPasswordScreen activation />
              </Suspense>
            }
          />
          {/*
            Signing, inside the panel and behind the creator's session.

            `RequireOnboarding` rather than `RequireOnboarded`: signing IS an
            onboarding step, so gating it on completed onboarding would be a
            deadlock. It also means a creator who has finished cannot wander
            back here — the guard sends them to the portal, and a signed
            agreement is read from the Contracts tab instead.
          */}
          <Route
            path="/creator/contracts/:id/sign"
            element={
              <CreatorAuthProvider>
                <RequireOnboarding>
                  <Suspense fallback={<ScreenFallback />}>
                    <CreatorSignScreen />
                  </Suspense>
                </RequireOnboarding>
              </CreatorAuthProvider>
            }
          />

          {/*
            The onboarding video — the step that completes onboarding.

            `RequireOnboarding` rather than `RequireOnboarded`, for the same
            reason as signing: gating the thing that finishes onboarding on
            having finished it is a deadlock.
          */}
          <Route
            path="/creator/watch-video"
            element={
              <CreatorAuthProvider>
                <RequireOnboarding>
                  <Suspense fallback={<ScreenFallback />}>
                    <CreatorWatchVideoScreen />
                  </Suspense>
                </RequireOnboarding>
              </CreatorAuthProvider>
            }
          />

          <Route
            path="/creator/book-call"
            element={
              <CreatorAuthProvider>
                <RequireOnboarding>
                  <Suspense fallback={<ScreenFallback />}>
                    <CreatorBookCallScreen />
                  </Suspense>
                </RequireOnboarding>
              </CreatorAuthProvider>
            }
          />

          {/*
            The portal's five tabs.

            `/creator` redirects to Videos rather than rendering its own screen:
            publishing is the action this app exists to push down to creators,
            so it is the one they should land on.
          */}
          <Route path="/creator" element={<Navigate to="/creator/videos" replace />} />
          {[
            { path: '/creator/videos', Screen: CreatorVideosScreen },
            { path: '/creator/payments', Screen: CreatorPaymentsScreen },
            { path: '/creator/contracts', Screen: CreatorContractsScreen },
            { path: '/creator/products', Screen: CreatorProductsScreen },
            { path: '/creator/support', Screen: CreatorSupportScreen },
          ].map(({ path, Screen }) => (
            <Route
              key={path}
              path={path}
              element={
                <CreatorAuthProvider>
                  <RequireOnboarded>
                    <Suspense fallback={<ScreenFallback />}>
                      <Screen />
                    </Suspense>
                  </RequireOnboarded>
                </CreatorAuthProvider>
              }
            />
          ))}

          {['/pipeline', '/pipeline/stats'].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute requires="pipeline.view">
                  {/* ProtectedRoute's skeleton covers the session check; this
                      one only shows for the brief chunk fetch. */}
                  <Suspense fallback={<ScreenFallback />}>
                    <PipelineScreen />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          ))}

          <Route
            path="/revenue"
            element={
              <ProtectedRoute requires="revenue.view">
                <Suspense fallback={<ScreenFallback />}>
                  <RevenueScreen />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/videos"
            element={
              <ProtectedRoute requires="videos.view">
                <Suspense fallback={<ScreenFallback />}>
                  <VideosScreen />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/investor-updates"
            element={
              <ProtectedRoute requires="revenue.view">
                <Navigate to="/revenue?view=arr" replace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/chrome-extension"
            element={
              <ProtectedRoute requires="pipeline.view">
                <Suspense fallback={<ScreenFallback />}>
                  <ChromeExtensionScreen />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/:section"
            element={
              <ProtectedRoute>
                <Suspense fallback={<ScreenFallback />}>
                  <SettingsScreen />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inbound"
            element={
              <ProtectedRoute requires="inbound.view">
                <Suspense fallback={<ScreenFallback />}>
                  <InboundScreen />
                </Suspense>
              </ProtectedRoute>
            }
          />


          <Route
            path="/support"
            element={
              <ProtectedRoute requires="support.view">
                <Suspense fallback={<ScreenFallback />}>
                  <SupportScreen />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/*
            Bare /settings resolves per-user rather than to a fixed tab: a role
            with queue access but not campaigns would otherwise be redirected
            straight into a screen it is not allowed to open, and bounced back
            out again by ProtectedRoute.
          */}
          <Route
            path="/admin/employee-onboarding"
            element={
              <ProtectedRoute requires="employeeOnboarding.view">
                <Suspense fallback={<ScreenFallback />}>
                  <EmployeeOnboardingAdminScreen />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route path="/settings" element={<SettingsHome />} />

          {/* Old bookmarks and the OAuth callback both used to land on
              /dashboard — keep them working rather than 404ing. */}
          <Route path="/dashboard/*" element={<Navigate to="/pipeline" replace />} />
          <Route path="/dashboard" element={<Navigate to="/pipeline" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <StaffSupportDock />
        <CreatorChatRoute />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

function CreatorChatRoute() {
  const { pathname } = useLocation();
  if (!['/creator/videos', '/creator/payments', '/creator/contracts', '/creator/products', '/creator/support'].includes(pathname)) return null;
  return <CreatorAuthProvider><CreatorChatGuard /></CreatorAuthProvider>;
}

function CreatorChatGuard() {
  const { isOnboarded } = useCreatorAuth();
  return isOnboarded ? <CreatorSupportWidget /> : null;
}

function ScreenFallback() {
  return <div className="bg-background h-screen" />;
}

/**
 * Where `/settings` lands.
 *
 * The first settings tab this role can open, or the app's home when it can
 * open none — which is the state of a pipeline-only member, and for whom a
 * "Settings" URL should not be a dead end.
 */
/**
 * Forward a legacy /sign/:token link to the account step.
 *
 * `replace` so the dead URL does not sit in history behind them, and a
 * component rather than an inline `<Navigate>` because the token has to be read
 * out of the params to be carried across.
 */
function LegacySignRedirect() {
  const { token = '' } = useParams();
  return <Navigate to={`/creator/join/${token}`} replace />;
}

function SettingsHome() {
  const { settingsGroups, homeRoute } = usePermissions();
  return <Navigate to={settingsGroups[0]?.route ?? homeRoute ?? '/pipeline'} replace />;
}
